/*
  Hero "Aracini Sec" module.

  Three entry points into the Sanal Garaj (see vehicle-garage.js):
    1. Faceted dropdowns: Marka / Model / Yil / Kasa Tipi / Motor Hacmi - each
       field is independently selectable (no cascade requirement). Picking
       any one field narrows the options of the others, but the user can
       search with just one field filled in (e.g. only Yil, or only Motor
       Hacmi) - there is no "fill everything" restriction.
    2. VIN (sasi no) lookup
    3. Free-text product search

  Precise client-side fitment filtering (vehicle-garage.js's matchesProduct)
  only understands modelId/engineId, since that is what product metafields
  encode. A partial search (e.g. year-only) can't drive that exact grid
  filter, so onSubmit() below falls back to native Shopify search built from
  whichever fields are filled - the same mechanism the VIN and product-search
  tabs already use - and only updates the Sanal Garaj (precise grid filter)
  when a model has actually been identified.

  VIN decoding needs a real fitment/VIN-decode service (NHTSA-style API or a
  Shopify app) that this theme does not ship with. `decodeVin()` below is the
  integration point: it currently just forwards the VIN to native search so
  the UI has a working fallback, but should be swapped for a real API call.
*/
(function () {
  class VehicleSelector extends HTMLElement {
    constructor() {
      super();

      this.data = null;
      this.records = [];
      this.dataUrl = this.getAttribute('data-source');
      this.redirectUrl = this.getAttribute('data-redirect-url') || theme.routes.root_url;

      this.tabButtons = Array.from(this.querySelectorAll('[data-vehicle-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-vehicle-panel]'));

      this.selects = {
        make: this.querySelector('[data-vehicle-make]'),
        model: this.querySelector('[data-vehicle-model]'),
        year: this.querySelector('[data-vehicle-year]'),
        bodyType: this.querySelector('[data-vehicle-body-type]'),
        engine: this.querySelector('[data-vehicle-engine]')
      };
      this.submitButton = this.querySelector('[data-vehicle-submit]');

      this.vinForm = this.querySelector('[data-vin-form]');
      this.vinInput = this.querySelector('[data-vin-input]');
      this.vinError = this.querySelector('[data-vin-error]');

      // Each select has a matching custom `details is="details-dropdown"` combobox
      // (see snippets/vehicle-selector-field.liquid) so the popup can be styled to
      // match the dark card instead of the browser's native <select> list. The
      // select stays the source of truth; syncCombo() mirrors it into the combo.
      this.combos = {
        make: this.querySelector('[data-vehicle-combo-make]'),
        model: this.querySelector('[data-vehicle-combo-model]'),
        year: this.querySelector('[data-vehicle-combo-year]'),
        bodyType: this.querySelector('[data-vehicle-combo-body-type]'),
        engine: this.querySelector('[data-vehicle-combo-engine]')
      };

      this.tabButtons.forEach((button) => button.addEventListener('click', this.onTabClick.bind(this)));

      Object.entries(this.selects).forEach(([key, select]) => {
        if (select) select.addEventListener('change', this.onFieldChange.bind(this));

        const combo = this.combos[key];
        if (!combo) return;

        combo.querySelector('[data-combo-list]').addEventListener('click', (event) => {
          const option = event.target.closest('[data-combo-option]');
          if (option) this.onComboSelect(key, option.getAttribute('data-value'));
        });

        combo.querySelector('[data-combo-trigger]').addEventListener('click', (event) => {
          if (select && select.disabled) event.preventDefault();
        });
      });

      if (this.submitButton) this.submitButton.addEventListener('click', this.onSubmit.bind(this));
      if (this.vinForm) this.vinForm.addEventListener('submit', this.onVinSubmit.bind(this));

      this.loadData();
    }

    async loadData() {
      try {
        const response = await fetch(this.dataUrl);
        const json = await response.json();
        this.data = json.makes;

        this.records = [];
        this.data.forEach((make) => {
          make.models.forEach((model) => {
            this.records.push({
              makeId: make.id,
              makeName: make.name,
              modelId: model.id,
              modelName: model.name,
              years: model.years,
              bodyTypes: model.bodyTypes,
              engines: model.engines
            });
          });
        });

        this.refreshOptions();
        this.updateSubmitState();
      }
      catch (error) {
        console.error('Vehicle data could not be loaded', error);
      }
    }

    onTabClick(event) {
      const target = event.currentTarget;
      const tab = target.getAttribute('data-vehicle-tab');

      this.tabButtons.forEach((button) => {
        const isActive = button === target;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.getAttribute('data-vehicle-panel') !== tab;
      });
    }

    getCriteria() {
      return {
        make: this.selects.make?.value || '',
        model: this.selects.model?.value || '',
        year: this.selects.year?.value || '',
        bodyType: this.selects.bodyType?.value || '',
        engine: this.selects.engine?.value || ''
      };
    }

    filterRecords(criteria) {
      return this.records.filter((record) => {
        if (criteria.make && record.makeId !== criteria.make) return false;
        if (criteria.model && record.modelId !== criteria.model) return false;
        if (criteria.year && !record.years.includes(Number(criteria.year))) return false;
        if (criteria.bodyType && !record.bodyTypes.some((item) => item.id === criteria.bodyType)) return false;
        if (criteria.engine && !record.engines.some((item) => item.id === criteria.engine)) return false;
        return true;
      });
    }

    optionsForField(key, records) {
      const map = new Map();

      records.forEach((record) => {
        if (key === 'make') map.set(record.makeId, record.makeName);
        else if (key === 'model') map.set(record.modelId, record.modelName);
        else if (key === 'year') record.years.forEach((year) => map.set(year, String(year)));
        else if (key === 'bodyType') record.bodyTypes.forEach((item) => map.set(item.id, item.name));
        else if (key === 'engine') record.engines.forEach((item) => map.set(item.id, item.name));
      });

      const entries = Array.from(map, ([value, label]) => ({ value, label }));
      entries.sort((a, b) => (key === 'year' ? b.value - a.value : a.label.localeCompare(b.label, 'tr')));
      return entries;
    }

    refreshOptions() {
      const keys = ['make', 'model', 'year', 'bodyType', 'engine'];
      const criteria = this.getCriteria();

      // Pass 1: drop any selection that is no longer compatible with the others.
      keys.forEach((key) => {
        if (!criteria[key]) return;
        const others = { ...criteria, [key]: '' };
        const options = this.optionsForField(key, this.filterRecords(others));
        const stillValid = options.some((option) => String(option.value) === String(criteria[key]));
        if (!stillValid) criteria[key] = '';
      });

      // Pass 2: rebuild each field's option list from the (possibly trimmed) criteria.
      keys.forEach((key) => {
        const select = this.selects[key];
        if (!select) return;

        const others = { ...criteria, [key]: '' };
        const options = this.optionsForField(key, this.filterRecords(others));

        select.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = this.strings()[key];
        select.appendChild(placeholder);

        options.forEach((option) => {
          const el = document.createElement('option');
          el.value = option.value;
          el.textContent = option.label;
          select.appendChild(el);
        });

        select.value = criteria[key];
        select.disabled = false;
        this.syncCombo(key);
      });
    }

    syncCombo(key) {
      const select = this.selects[key];
      const combo = this.combos[key];
      if (!select || !combo) return;

      const list = combo.querySelector('[data-combo-list]');
      const valueEl = combo.querySelector('[data-combo-value]');

      list.innerHTML = '';
      Array.from(select.options).forEach((opt) => {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'vehicle-selector__combo-option';
        if (opt.value === '') button.classList.add('vehicle-selector__combo-option--clear');
        button.setAttribute('role', 'option');
        button.setAttribute('data-combo-option', '');
        button.setAttribute('data-value', opt.value);
        button.textContent = opt.textContent;

        if (opt.value === select.value) {
          button.classList.add('is-selected');
          button.setAttribute('aria-selected', 'true');
        }

        li.appendChild(button);
        list.appendChild(li);
      });

      valueEl.textContent = select.selectedOptions[0]?.textContent || '';
      combo.classList.toggle('is-disabled', select.disabled);
    }

    onComboSelect(key, value) {
      const select = this.selects[key];
      const combo = this.combos[key];
      if (!select) return;

      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      if (combo) combo.open = false;
    }

    onFieldChange() {
      this.refreshOptions();
      this.updateSubmitState();
    }

    updateSubmitState() {
      if (!this.submitButton) return;
      this.submitButton.disabled = !this.data;
    }

    onSubmit(event) {
      event.preventDefault();

      const criteria = this.getCriteria();
      const labelOf = (key) => this.selects[key]?.selectedOptions[0]?.textContent;

      const parts = [];
      if (criteria.make) parts.push(labelOf('make'));
      if (criteria.model) parts.push(labelOf('model'));
      if (criteria.year) parts.push(criteria.year);
      if (criteria.bodyType) parts.push(labelOf('bodyType'));
      if (criteria.engine) parts.push(labelOf('engine'));

      if (parts.length === 0) {
        window.location.href = this.redirectUrl;
        return;
      }

      if (criteria.model) {
        const record = this.records.find((item) => item.modelId === criteria.model
          && (!criteria.make || item.makeId === criteria.make));

        if (record) {
          theme.vehicleGarage.set({
            makeId: record.makeId,
            makeName: record.makeName,
            modelId: record.modelId,
            modelName: record.modelName,
            year: criteria.year,
            bodyTypeId: criteria.bodyType,
            bodyTypeName: criteria.bodyType ? labelOf('bodyType') : '',
            engineId: criteria.engine,
            engineName: criteria.engine ? labelOf('engine') : '',
            label: parts.join(' · ')
          });
        }
      }

      const url = new URL(theme.routes.search_url, window.location.origin);
      url.searchParams.set('q', parts.join(' '));
      url.searchParams.set('type', 'product');
      window.location.href = url.toString();
    }

    onVinSubmit(event) {
      event.preventDefault();
      if (this.vinError) this.vinError.hidden = true;

      const vin = this.vinInput.value.trim().toUpperCase();
      if (vin.length < 11) {
        if (this.vinError) this.vinError.hidden = false;
        return;
      }

      this.decodeVin(vin);
    }

    decodeVin(vin) {
      // Integration point: replace with a real VIN-decode API / Shopify app call.
      // Until then, forward the VIN to native search so the user still gets results.
      const url = new URL(`${theme.routes.search_url}`, window.location.origin);
      url.searchParams.set('q', vin);
      url.searchParams.set('options[prefix]', 'last');
      window.location.href = url.toString();
    }

    strings() {
      return {
        make: this.getAttribute('data-string-select-make') || 'Marka Seçin',
        model: this.getAttribute('data-string-select-model') || 'Model Seçin',
        year: this.getAttribute('data-string-select-year') || 'Yıl Seçin',
        bodyType: this.getAttribute('data-string-select-body-type') || 'Kasa Tipi Seçin',
        engine: this.getAttribute('data-string-select-engine') || 'Motor Hacmi Seçin'
      };
    }
  }
  customElements.define('vehicle-selector', VehicleSelector);
})();
